import { motion } from 'framer-motion';
import {
    ArrowRight,
    Calendar,
    Clock,
    ExternalLink,
    Search,
    Tag,
    TrendingUp,
    User
} from 'lucide-react';
import { useState } from 'react';

const Blog = () => {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    const categories = [
        { id: 'all', name: 'All Posts', count: 24 },
        { id: 'product', name: 'Product Updates', count: 8 },
        { id: 'technical', name: 'Technical Deep Dives', count: 6 },
        { id: 'industry', name: 'Industry Insights', count: 5 },
        { id: 'company', name: 'Company News', count: 5 }
    ];

    const featuredPost = {
        id: 'w3c-compliance-update',
        title: 'Documint ID Achieves Full W3C Verifiable Credentials Specification Compliance',
        excerpt: 'We are excited to announce that our platform now supports the complete W3C Verifiable Credentials Data Model v2.0, ensuring maximum interoperability and future-proofing for all implementations.',
        author: 'Sarah Chen',
        role: 'Head of Product',
        publishDate: '2024-03-15',
        readTime: '5 min read',
        category: 'Product Updates',
        featured: true,
        image: '/blog/w3c-compliance.jpg'
    };

    const blogPosts = [
        {
            id: 'ssi-healthcare-revolution',
            title: 'How Self-Sovereign Identity is Revolutionizing Healthcare Data Management',
            excerpt: 'Explore how SSI technology is transforming patient data ownership, privacy, and interoperability in healthcare systems worldwide.',
            author: 'Dr. Michael Rodriguez',
            role: 'Healthcare Solutions Lead',
            publishDate: '2024-03-12',
            readTime: '8 min read',
            category: 'Industry Insights',
            tags: ['Healthcare', 'Privacy', 'Interoperability']
        },
        {
            id: 'technical-deep-dive-did-methods',
            title: 'Technical Deep Dive: Understanding Different DID Methods and When to Use Them',
            excerpt: 'A comprehensive guide to choosing the right DID method for your use case, comparing did:key, did:web, did:ion, and more.',
            author: 'Alex Thompson',
            role: 'Senior Blockchain Engineer',
            publishDate: '2024-03-10',
            readTime: '12 min read',
            category: 'Technical Deep Dives',
            tags: ['DID', 'Blockchain', 'Technical']
        },
        {
            id: 'kyc-transformation-fintech',
            title: 'The Future of KYC: How Fintech Companies are Embracing Verifiable Credentials',
            excerpt: 'Financial institutions are reducing onboarding time by 90% while improving security using verifiable credential technology.',
            author: 'Jennifer Park',
            role: 'Financial Services Director',
            publishDate: '2024-03-08',
            readTime: '6 min read',
            category: 'Industry Insights',
            tags: ['KYC', 'Fintech', 'Compliance']
        },
        {
            id: 'api-v2-release',
            title: 'Introducing Documint ID API v2.0: Enhanced Performance and New Features',
            excerpt: 'Our latest API release includes improved response times, batch operations, webhooks, and enhanced security features.',
            author: 'David Kim',
            role: 'Lead API Developer',
            publishDate: '2024-03-05',
            readTime: '4 min read',
            category: 'Product Updates',
            tags: ['API', 'Performance', 'Features']
        },
        {
            id: 'education-credentials-case-study',
            title: 'Case Study: How Universities are Issuing Tamper-Proof Digital Diplomas',
            excerpt: 'Learn how leading educational institutions are eliminating diploma fraud and enabling instant verification with blockchain-backed credentials.',
            author: 'Prof. Lisa Wang',
            role: 'Education Partnerships',
            publishDate: '2024-03-03',
            readTime: '7 min read',
            category: 'Industry Insights',
            tags: ['Education', 'Diplomas', 'Case Study']
        },
        {
            id: 'security-best-practices',
            title: 'Security Best Practices for Implementing Self-Sovereign Identity',
            excerpt: 'Essential security considerations when deploying SSI solutions, including key management, privacy protection, and threat mitigation.',
            author: 'Robert Chen',
            role: 'Chief Security Officer',
            publishDate: '2024-03-01',
            readTime: '10 min read',
            category: 'Technical Deep Dives',
            tags: ['Security', 'Best Practices', 'Implementation']
        },
        {
            id: 'company-series-b',
            title: 'Documint Raises $25M Series B to Accelerate Global SSI Adoption',
            excerpt: 'With this funding, we will expand our global team, enhance our platform capabilities, and accelerate adoption across new markets.',
            author: 'Emily Johnson',
            role: 'CEO & Co-founder',
            publishDate: '2024-02-28',
            readTime: '3 min read',
            category: 'Company News',
            tags: ['Funding', 'Growth', 'Company']
        },
        {
            id: 'mobile-wallet-sdk-launch',
            title: 'Launching Our Mobile Wallet SDK: Build SSI Apps in Minutes',
            excerpt: 'Our new React Native SDK makes it easier than ever to integrate self-sovereign identity capabilities into mobile applications.',
            author: 'Maria Garcia',
            role: 'Mobile SDK Lead',
            publishDate: '2024-02-25',
            readTime: '5 min read',
            category: 'Product Updates',
            tags: ['Mobile', 'SDK', 'Development']
        }
    ];

    const filteredPosts = blogPosts.filter(post => {
        const matchesCategory = selectedCategory === 'all' || post.category.toLowerCase().replace(' ', '') === selectedCategory;
        const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
        return matchesCategory && matchesSearch;
    });

    return (
        <div className="min-h-screen pt-20">
            {/* Hero Section */}
            <section className="bg-surface-100 py-20">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-12"
                    >
                        <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                            Insights &
                            <span className="text-neon-300"> Updates</span>
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                            Stay informed about the latest developments in Self-Sovereign Identity, product updates, 
                            and insights from our team of experts.
                        </p>

                        {/* Search */}
                        <div className="max-w-md mx-auto relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Search articles..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 bg-surface-200/40 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-neon-300 focus:border-transparent"
                            />
                        </div>
                    </motion.div>

                    {/* Categories */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="flex flex-wrap justify-center gap-4"
                    >
                        {categories.map((category) => (
                            <button
                                key={category.id}
                                onClick={() => setSelectedCategory(category.id)}
                                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                                    selectedCategory === category.id
                                        ? 'bg-neon-300 text-surface-100'
                                        : 'bg-surface-200/40 text-gray-300 hover:bg-surface-200/60'
                                }`}
                            >
                                {category.name}
                                <span className="ml-2 text-sm opacity-75">({category.count})</span>
                            </button>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Featured Post */}
            <section className="py-16 bg-white">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="mb-8"
                    >
                        <div className="flex items-center space-x-2 mb-4">
                            <TrendingUp className="w-5 h-5 text-primary-600" />
                            <span className="text-sm font-semibold text-primary-600 uppercase tracking-wide">
                                Featured Article
                            </span>
                        </div>
                    </motion.div>

                    <motion.article
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="bg-gradient-to-r from-primary-50 to-neon-50 rounded-2xl p-8 cursor-pointer hover:shadow-lg transition-shadow"
                    >
                        <div className="grid lg:grid-cols-3 gap-8 items-center">
                            <div className="lg:col-span-2">
                                <div className="flex items-center space-x-4 mb-4">
                                    <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                                        {featuredPost.category}
                                    </span>
                                    <div className="flex items-center text-gray-600 text-sm space-x-4">
                                        <div className="flex items-center space-x-1">
                                            <Calendar className="w-4 h-4" />
                                            <span>{featuredPost.publishDate}</span>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                            <Clock className="w-4 h-4" />
                                            <span>{featuredPost.readTime}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                                    {featuredPost.title}
                                </h2>
                                
                                <p className="text-gray-600 mb-6 text-lg">
                                    {featuredPost.excerpt}
                                </p>
                                
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
                                            <User className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <div className="font-semibold text-gray-900">{featuredPost.author}</div>
                                            <div className="text-sm text-gray-600">{featuredPost.role}</div>
                                        </div>
                                    </div>
                                    
                                    <button className="flex items-center text-primary-600 font-medium hover:text-primary-700 transition-colors">
                                        Read Article
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </button>
                                </div>
                            </div>
                            
                            <div className="bg-gray-200 rounded-xl aspect-video flex items-center justify-center">
                                <div className="text-gray-500 text-center">
                                    <ExternalLink className="w-12 h-12 mx-auto mb-2" />
                                    <div className="text-sm">Featured Image</div>
                                </div>
                            </div>
                        </div>
                    </motion.article>
                </div>
            </section>

            {/* Blog Posts Grid */}
            <section className="py-16 bg-gray-50">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="mb-12"
                    >
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">
                            Latest Articles
                        </h2>
                        <p className="text-gray-600">
                            {filteredPosts.length} articles found
                            {selectedCategory !== 'all' && ` in ${categories.find(c => c.id === selectedCategory)?.name}`}
                            {searchQuery && ` matching "${searchQuery}"`}
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {filteredPosts.map((post, index) => (
                            <motion.article
                                key={post.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                className="bg-white rounded-xl p-6 hover:shadow-lg transition-shadow cursor-pointer"
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                                        {post.category}
                                    </span>
                                    <div className="flex items-center text-gray-500 text-sm space-x-1">
                                        <Clock className="w-3 h-3" />
                                        <span>{post.readTime}</span>
                                    </div>
                                </div>
                                
                                <h3 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-2">
                                    {post.title}
                                </h3>
                                
                                <p className="text-gray-600 mb-4 line-clamp-3">
                                    {post.excerpt}
                                </p>
                                
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {post.tags.map((tag, tagIndex) => (
                                        <span
                                            key={tagIndex}
                                            className="flex items-center text-xs text-gray-500"
                                        >
                                            <Tag className="w-3 h-3 mr-1" />
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                                
                                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                                            <User className="w-4 h-4 text-gray-600" />
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium text-gray-900">{post.author}</div>
                                            <div className="text-xs text-gray-500">{post.publishDate}</div>
                                        </div>
                                    </div>
                                    
                                    <ArrowRight className="w-4 h-4 text-gray-400" />
                                </div>
                            </motion.article>
                        ))}
                    </div>

                    {filteredPosts.length === 0 && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-center py-12"
                        >
                            <div className="text-gray-400 mb-4">
                                <Search className="w-12 h-12 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold mb-2">No articles found</h3>
                                <p>Try adjusting your search or category filter</p>
                            </div>
                            <button
                                onClick={() => {
                                    setSearchQuery('');
                                    setSelectedCategory('all');
                                }}
                                className="btn-secondary"
                            >
                                Clear Filters
                            </button>
                        </motion.div>
                    )}
                </div>
            </section>

            {/* Newsletter CTA */}
            <section className="py-20 bg-surface-100">
                <div className="container-custom text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                            Never Miss an Update
                        </h2>
                        <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                            Subscribe to our newsletter for the latest insights on Self-Sovereign Identity, 
                            product updates, and industry developments.
                        </p>
                        <button className="btn-primary inline-flex items-center">
                            Subscribe to Newsletter
                            <ArrowRight size={20} className="ml-2" />
                        </button>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default Blog;